// FILE: src/components/HomelinksTable.jsx
// A student's homelink progress as a table (now used by the admin
// compliance record). Props are unchanged: pass `homelinksProp` +
// `homelinksViewedProp` to show someone else's progress (admin), or nothing
// to use the signed-in student's own (Redux). The look lives in
// admin/LessonProgressTable.
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { selectLessonsBlocked } from "../features/timeSlice";
import { fetchStudentResources } from "../features/resourceSlice";
import { EmptyState, ErrorState, LoadingState } from "./ui";
import LessonProgressTable from "./admin/LessonProgressTable";
import { mergeLessons } from "./student/lessons";
import { BLOCKED_REASON } from "./student/LessonList";

/**
 * @param {Object[]} [homelinksProp] - Homelinks to show (skips the Redux fetch).
 * @param {Object[]} [homelinksViewedProp] - Their progress records (skips Redux).
 * @param {"HOMELINK"} [resourceType="HOMELINK"] - What to fetch when no props are given.
 * @param {boolean} [viewOnly=false] - Hide the row actions.
 */
export default function HomelinksTable({ homelinksProp, homelinksViewedProp, resourceType = "HOMELINK", viewOnly = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  // Daily cap, break or ended enrolment: the server would refuse the lesson (WP-3).
  const blocked = useSelector(selectLessonsBlocked);
  const { homelinks: reduxHomelinks, loading: resourcesLoading, error: resourcesError } = useSelector((state) => state.resources);
  const { homelinksViewed: reduxHomelinksViewed } = useSelector((state) => state.progress);

  const homelinks = homelinksProp || reduxHomelinks;
  const homelinksViewed = homelinksViewedProp || reduxHomelinksViewed;

  useEffect(() => {
    if (!homelinksProp) dispatch(fetchStudentResources(resourceType));
  }, [homelinksProp, resourceType, dispatch]);

  const rows = useMemo(() => mergeLessons(homelinks || [], homelinksViewed || [], "homelinkId"), [homelinks, homelinksViewed]);

  if (!homelinksProp && resourcesLoading && !rows.length) return <LoadingState variant="table" rows={4} label="Loading homelinks…" />;
  if (!homelinksProp && resourcesError && !rows.length) return <ErrorState compact error={resourcesError} />;
  if (!rows.length) return <EmptyState compact title="No homelinks yet" description="Homelinks appear here when they're published." />;

  return (
    <>
      <LessonProgressTable
        rows={rows}
        kind="homelink"
        viewOnly={viewOnly}
        blocked={blocked}
        untimed={!!profile?.untimed /* audited admin flag, server-honoured (D8) */}
        blockedReasonId="lessons-blocked-reason-homelinks"
        onOpen={(row) => navigate(`/student/resources/${row._id}?resourceType=homelink`)}
      />
      {blocked && !viewOnly && (
        <Typography id="lessons-blocked-reason-homelinks" sx={{ mt: 1.5, color: "text.secondary" }} data-testid="lessons-blocked-homelinks">
          {BLOCKED_REASON[blocked]}
        </Typography>
      )}
    </>
  );
}
